import {z} from "zod/v4";
import {PickupOrDropoff, Transfer, VenueStub} from "../rpc/webrpc-client.gen.ts";
import {BaseTimelineScene} from "../timeline/timeline.tsx";
import {makeVenuePlace, VenuePlace} from "../tq/tqxx.tsx";
import {useTranslation} from "react-i18next";
import {FontAwesomeIconProps} from "@fortawesome/react-fontawesome";
import {faTruckLoading} from "@fortawesome/pro-regular-svg-icons";
import {SceneTileContent} from "../dispatch/DispatchTile.tsx";
import {Box} from "@mui/joy";
import {VenueCapsule} from "../venue/capsule.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {TimelineTimePoint} from "../timeline/timelineTimeCoordinate.tsx";

const TransferSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("transfer"),
    subject: Transfer,
    where: VenuePlace,
    when: TimelineTimePoint,
});

export const TransferStartedScene = z.object({
    ...TransferSceneBase.shape,
    direction: PickupOrDropoff,
    sceneFlavor: z.literal("start"),
});
export type TransferStartedScene = z.infer<typeof TransferStartedScene>;

export const TransferEndedScene = z.object({
    ...TransferSceneBase.shape,
    direction: PickupOrDropoff,
    sceneFlavor: z.literal("end"),
});
export type TransferEndedScene = z.infer<typeof TransferEndedScene>;

export const TransferScene =
    z.discriminatedUnion("sceneFlavor", [TransferStartedScene, TransferEndedScene]);
export type TransferScene = z.infer<typeof TransferScene>;

export function synthTransferScenes({transfers}: { transfers: Transfer[] }): TransferScene[] {
    const rv: TransferScene[] = [];

    for (const xfer of transfers) {
        if (xfer.onloadStarted && xfer.onloadVenue) {
            const scene: TransferStartedScene = {
                subjectFlavor: "transfer",
                subject: xfer,
                direction: PickupOrDropoff.enum.PICKUP,
                sceneFlavor: "start",
                where: makeVenuePlace(xfer.onloadVenue),
                when: {at: xfer.onloadStarted},
            }
            rv.push(scene);
        }
        if (xfer.onloadEnded && xfer.onloadVenue) {
            const scene: TransferEndedScene = {
                subjectFlavor: "transfer",
                subject: xfer,
                direction: PickupOrDropoff.enum.PICKUP,
                sceneFlavor: "end",
                where: makeVenuePlace(xfer.onloadVenue),
                when: {at: xfer.onloadEnded},
            }
            rv.push(scene);
        }

        if (xfer.offloadStarted && xfer.offloadVenue) {
            const scene: TransferStartedScene = {
                subjectFlavor: "transfer",
                subject: xfer,
                direction: PickupOrDropoff.enum.DROPOFF,
                sceneFlavor: "start",
                where: makeVenuePlace(xfer.offloadVenue),
                when: {at: xfer.offloadStarted},
            }
            rv.push(scene);
        }
        if (xfer.offloadEnded && xfer.offloadVenue) {
            const scene: TransferEndedScene = {
                subjectFlavor: "transfer",
                subject: xfer,
                direction: PickupOrDropoff.enum.DROPOFF,
                sceneFlavor: "end",
                where: makeVenuePlace(xfer.offloadVenue),
                when: {at: xfer.offloadEnded},
            }
            rv.push(scene);
        }
    }

    return rv;
}

export function useTransferSceneColor(scene?: TransferScene) {
    if (!scene) {
        return undefined;
    }
    return scene.sceneFlavor === "start" ? "primary" : "success";
}

export function TransferSceneTileContent({scene, color: inColor, variant, children}: SceneViewProps<TransferScene>) {
    const {t} = useTranslation();
    const transfer = scene.subject;
    const intrinsicColor = useTransferSceneColor(scene);

    const when = scene.when;
    let color = inColor ?? intrinsicColor;
    let label;
    const icon: FontAwesomeIconProps = {icon: faTruckLoading};
    let whereVenue;

    if (scene.direction === "PICKUP") {
        whereVenue = VenueStub.parse(transfer.onloadVenue);
        if (scene.sceneFlavor === "start") {
            if (!color) {
                color = "primary";
            }
            label = t("driver.startOnloading");
        } else {
            label = t("driver.onloaded");
        }
    } else {
        whereVenue = VenueStub.parse(transfer.offloadVenue);
        icon.flip = "horizontal";
        if (scene.sceneFlavor === "start") {
            if (!color) {
                color = "primary";
            }
            label = t("driver.offloading");
        } else {
            label = t("driver.offloaded");
        }
    }

    return <SceneTileContent
        what={<Box>{label}</Box>}
        where={<VenueCapsule venue={whereVenue}/>}
        when={when}
        icon={icon}
        color={color} variant={variant}>
        {children}
    </SceneTileContent>
}

